import { defineStore } from 'pinia'
import env from '@/config'
import http from '@/services/http'
import router from '../router'
import { useAuthStore } from './auth'
import { useNavbarStore } from './navbar'
import { useAppStore } from './modules/app'

export const useHttpStore = defineStore('http', {
    // As tres instancias axios daqui foram para services/http.js (JIMEA-954).
    //
    // O env vem por import, nao por inject('env'): inject so' resolve durante o setup
    // de um componente, entao instanciar este store de outro lugar daria undefined.
    state: () => ({
        baseURL: env.api.appbox.baseURL
    }),
    actions: {
        async sendAboutApp(data) {
            const mountedData = {
                update_section_id: 1,
                name: data.name,
                app_description: data.app_description,
                support_email: data.support_email,
                billing_model: data.billing_model,
                is_private: data.is_private,
                app_billing_rate: data.billing_model === '1' ? data.app_billing_rate : ''
            }

            return await http
                .post(`${this.baseURL}/app/`, mountedData)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw error
                })
        },
        async getCreationLimit() {
            return await http
                .get(`${this.baseURL}/app/creation-limit`)
                .then((response) => response.data.data)
                .catch(() => null)
        },
        async sendImagesApp(appID, data) {
            const mountedData = {
                update_section_id: 3,
                app_id: appID,
                logo: data.logo
            }

            return await http
                .post(`${this.baseURL}/app/`, mountedData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async sendConfigToApp(appID, data) {
            const mountedData = {
                update_section_id: 2,
                app_id: appID,
                permissions: data.permissions
            }

            return await http
                .post(`${this.baseURL}/app/`, mountedData)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async sendDeveloperInfosToApp(appID, data) {
            const mountedData = {
                id: appID,
                app_external_host: data.app_external_host,
                url_repository: data.url_repository,
                app_external_validation: data.app_external_validation
            }

            return await http
                .post(`${this.baseURL}/app/developer`, mountedData)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async editAboutApp(appID, data) {
            const mountedData = {
                update_section_id: 1,
                id: appID,
                ...data
            }

            return await http
                .post(`${this.baseURL}/app/update-app`, mountedData)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async editImagesApp(appID, data) {
            const appStore = useAppStore()

            const mountedData = {
                update_section_id: 3,
                id: appID,
                logo: appStore.app.logo ?? data.logo
            }

            return await http
                .post(`${this.baseURL}/app/update-app`, mountedData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async editConfigApp(appID, data = {}) {
            const appStore = useAppStore()

            const mountedData = {
                update_section_id: 2,
                id: appID,
                permissions: data.permissions ?? appStore.app.permissions,
                url_repository: data.systemURL ?? appStore.app.systemURL,
                app_external_host: data.host ?? appStore.app.host,
                app_external_validation: data.validationURL ?? appStore.app.validationURL
            }

            return await http
                .post(`${this.baseURL}/app/update-app`, mountedData)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async getPermissions() {
            return await http
                .get(`${this.baseURL}/apps/permissions`)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async getApps() {
            return await http
                .get(`${this.baseURL}/apps`)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    if (error.response.status === 401) {
                        router.push({ name: 'login' })
                    }

                    throw new Error(error.response)
                })
        },
        async getAppCredentials(appId) {
            return await http
                .get(`${this.baseURL}/app/credentials/${appId}`)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    if (error.response && error.response.status === 401) {
                        router.push({ name: 'login' })
                    }

                    throw error
                })
        },
        async revealAppCredential(appId, stage) {
            return await http
                .post(`${this.baseURL}/app/credentials/${appId}/reveal`, { stage })
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    if (error.response && error.response.status === 401) {
                        router.push({ name: 'login' })
                    }

                    // Re-throw o erro original para preservar error.response
                    // e permitir que o componente trate 409/404/422 (visualizacao unica)
                    throw error
                })
        },
        async regenerateAppCredential(appId, stage) {
            return await http
                .post(`${this.baseURL}/app/credentials/${appId}/regenerate`, { stage })
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    if (error.response && error.response.status === 401) {
                        router.push({ name: 'login' })
                    }

                    // Re-throw o erro original para preservar error.response (ex: 502 da Appmax)
                    throw error
                })
        },
        async confirmTwoFactorCode(challenge, code) {
            return await http
                .post(`/auth/user/confirm-2fa-code`, { challenge, code })
                .then((response) => response)
                .catch((error) => {
                    throw error
                })
        },
        async resendTwoFactorCode(challenge) {
            return await http
                .post(`/auth/user/send-2fa-code`, { challenge })
                .then((response) => response)
                .catch((error) => {
                    throw error
                })
        },
        async requestForgotPassword(email) {
            return await http
                .post(`${this.baseURL}/auth/user/request-reset-password`, {
                    email
                })
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async resetPassword(data) {
            return await http
                .post(`${this.baseURL}/auth/user/reset-password`, data)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    throw new Error(error.response)
                })
        },
        async validateToken(reset = false) {
            const authStore = useAuthStore()
            const navbarStore = useNavbarStore()

            return authStore.getAuthenticatedStatus
                ? await http
                      .get(`${this.baseURL}/validate-token`)
                      .then((response) => {
                          return response
                      })
                      .catch((error) => {
                          if (reset && authStore.getAuthenticatedStatus) {
                              authStore.clearSession()
                              navbarStore.setPublicMode()
                          }

                          // Erro original preservado: embrulhar em new Error(response)
                          // apagava o status e matava qualquer tratamento acima.
                          throw error
                      })
                : this
        },
        async getDraftApp() {
            const authStore = useAuthStore()

            return await http
                .get(`${this.baseURL}/apps/has-draft-app`)
                .then((response) => {
                    if (response.data.data === false) {
                        return false
                    }

                    return response
                })
                .catch((error) => {
                    if (error.response.status === 401) {
                        authStore.setStatusAuthentication(false)
                        router.push({ name: 'login' })
                    }

                    throw new Error(error.response)
                })
        },
        async getAppById(id) {
            return await http
                .get(`${this.baseURL}/app/${id}`)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    if (error.response.status === 401) {
                        router.push({ name: 'login' })
                    }

                    throw new Error(error.response)
                })
        },
        async updateAppStatus(appID, status) {
            const mountedData = {
                id: appID,
                status_id: status
            }

            return await http
                .post(`${this.baseURL}/app/update-app-status`, mountedData)
                .then((response) => {
                    return response
                })
                .catch((error) => {
                    // Re-throw o erro original para preservar error.response
                    // e permitir que os componentes inspecionem status HTTP (ex: 422)
                    throw error
                })
        },
        async adminMoveAppToHomolog(appID) {
            return await http
                .post(`${this.baseURL}/app/admin/${appID}/move-to-homolog`)
                .then((response) => response)
                .catch((error) => {
                    throw error
                })
        },
        async adminApproveApp(appID) {
            return await http
                .post(`${this.baseURL}/app/admin/${appID}/approve`)
                .then((response) => response)
                .catch((error) => {
                    throw error
                })
        },
        async getChangelogs() {
            return await http
                .get(`${this.baseURL}/changelogs`)
                .then(response => response.data.data)
                .catch(error => {
                    throw new Error(error.response)
                })
        },
        async adminPublishPrivateApp(appID) {
            return await http
                .post(`${this.baseURL}/app/admin/${appID}/publish-private`)
                .then((response) => response)
                .catch((error) => {
                    throw error
                })
        },
        async adminGenerateSandboxCredentials(appID) {
            return await http
                .post(`${this.baseURL}/app/admin/${appID}/credentials/sandbox`)
                .then((response) => response)
                .catch((error) => {
                    throw error
                })
        },
        async adminGenerateProductionCredentials(appID) {
            return await http
                .post(`${this.baseURL}/app/admin/${appID}/credentials/production`)
                .then((response) => response)
                .catch((error) => {
                    throw error
                })
        },
        async updateAppPermissions(appID, permissions) {
            return await http
                .post(`${this.baseURL}/app/${appID}/permissions`, { permissions })
                .then((response) => response)
                .catch((error) => {
                    throw error
                })
        }
    }
})
